import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { RotateCw, X, Check, Loader2, ZoomIn } from "lucide-react";

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", reject);
    img.src = url;
  });
}

async function getCroppedBlob(imageSrc, cropPixels, rotation, maxDim = 1080, quality = 0.82) {
  const image = await createImage(imageSrc);
  const rad = (rotation * Math.PI) / 180;

  // draw rotated image onto a safe canvas first
  const safe = document.createElement("canvas");
  const sctx = safe.getContext("2d");
  const maxSide = Math.ceil(Math.sqrt(image.width ** 2 + image.height ** 2));
  safe.width = maxSide;
  safe.height = maxSide;
  sctx.translate(maxSide / 2, maxSide / 2);
  sctx.rotate(rad);
  sctx.drawImage(image, -image.width / 2, -image.height / 2);

  // extract crop region (coords are relative to the rotated image's bounding box)
  const data = sctx.getImageData(
    Math.round((maxSide - image.width) / 2 + cropPixels.x),
    Math.round((maxSide - image.height) / 2 + cropPixels.y),
    Math.round(cropPixels.width),
    Math.round(cropPixels.height)
  );

  let outW = cropPixels.width;
  let outH = cropPixels.height;
  const scale = Math.min(1, maxDim / Math.max(outW, outH));
  const tmp = document.createElement("canvas");
  tmp.width = Math.round(cropPixels.width);
  tmp.height = Math.round(cropPixels.height);
  tmp.getContext("2d").putImageData(data, 0, 0);

  const out = document.createElement("canvas");
  out.width = Math.round(outW * scale);
  out.height = Math.round(outH * scale);
  const octx = out.getContext("2d");
  octx.imageSmoothingQuality = "high";
  octx.drawImage(tmp, 0, 0, out.width, out.height);

  return new Promise((resolve) => out.toBlob((b) => resolve(b), "image/jpeg", quality));
}

export default function PhotoEditor({ imageSrc, onCancel, onConfirm }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pixels, setPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onComplete = useCallback((_area, areaPixels) => setPixels(areaPixels), []);

  const confirm = async () => {
    if (!pixels) return;
    setSaving(true);
    try {
      const blob = await getCroppedBlob(imageSrc, pixels, rotation);
      await onConfirm(blob);
    } catch {
      await onConfirm(null); // canvas failed (e.g. Android memory) -> parent uploads original
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="absolute inset-0 z-[60] bg-[#0D0C12] flex flex-col" data-testid="photo-editor">
      <div className="h-14 px-4 flex items-center justify-between border-b border-white/10 shrink-0">
        <button data-testid="editor-cancel" onClick={onCancel} className="w-9 h-9 grid place-items-center text-slate-300 rounded-full active:bg-white/10"><X className="w-5 h-5" /></button>
        <span className="font-semibold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Edit photo</span>
        <button data-testid="editor-confirm" onClick={confirm} disabled={saving || !pixels} className="w-9 h-9 grid place-items-center text-rose-400 rounded-full active:bg-white/10 disabled:opacity-40">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
        </button>
      </div>

      <div className="relative flex-1 bg-black">
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={3 / 4}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onRotationChange={setRotation}
          onCropComplete={onComplete}
          showGrid
        />
      </div>

      <div className="p-5 pb-8 shrink-0 space-y-4 border-t border-white/10">
        <div className="flex items-center gap-3">
          <ZoomIn className="w-5 h-5 text-slate-400 shrink-0" />
          <input data-testid="editor-zoom" type="range" min={1} max={3} step={0.05} value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))} className="w-full accent-rose-500" />
        </div>
        <button data-testid="editor-rotate" onClick={() => setRotation((r) => (r + 90) % 360)}
          className="w-full h-11 rounded-xl bg-white/5 border border-white/10 text-slate-200 text-sm font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
          <RotateCw className="w-4 h-4" /> Rotate
        </button>
      </div>
    </div>
  );
}
